/** Circular hue/saturation colour-wheel picker with a lightness slider. */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { PointerEvent as ReactPointerEvent } from "react";

interface ColorWheelPickerProps {
  color: string;
  onChange: (hex: string) => void;
  label: string;
  highlighted?: boolean;
}

const WHEEL_SIZE = 168;
const WHEEL_RADIUS = WHEEL_SIZE / 2 - 6;
const PANEL_WIDTH = 208; // 13rem
const PANEL_HEIGHT = 278;
const VIEWPORT_PAD = 8;
const GAP = 8;

function hex2hsl(hex: string): [number, number, number] {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return [h * 360, s * 100, l * 100];
}

function hsl2hex(h: number, s: number, l: number): string {
  const sNorm = s / 100;
  const lNorm = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sNorm * Math.min(lNorm, 1 - lNorm);
  const f = (n: number) => lNorm - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (x: number) => Math.round(255 * x).toString(16).padStart(2, "0");
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`.toUpperCase();
}

export function ColorWheelPicker({ color, onChange, label, highlighted }: ColorWheelPickerProps) {
  const [open, setOpen] = useState(false);
  const [hue, setHue] = useState(0);
  const [sat, setSat] = useState(0);
  const [light, setLight] = useState(50);
  const [coords, setCoords] = useState<{ left: number; top: number; maxHeight: number } | null>(
    null,
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const draggingRef = useRef(false);
  const panelId = useId();

  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const left = Math.max(
      VIEWPORT_PAD,
      Math.min(rect.left, window.innerWidth - PANEL_WIDTH - VIEWPORT_PAD),
    );
    const belowTop = rect.bottom + GAP;
    const spaceBelow = window.innerHeight - belowTop - VIEWPORT_PAD;
    const spaceAbove = rect.top - GAP - VIEWPORT_PAD;
    const openAbove = spaceBelow < PANEL_HEIGHT && spaceAbove > spaceBelow;
    const available = Math.max(120, openAbove ? spaceAbove : spaceBelow);
    const maxHeight = Math.min(PANEL_HEIGHT, available);
    const top = openAbove
      ? Math.max(VIEWPORT_PAD, rect.top - GAP - maxHeight)
      : Math.min(belowTop, window.innerHeight - VIEWPORT_PAD - maxHeight);

    setCoords({ left, top: Math.max(VIEWPORT_PAD, top), maxHeight });
  }, []);

  // Start the wheel at the swatch's current colour whenever it opens.
  useEffect(() => {
    if (!open) return;
    const [h, s, l] = hex2hsl(color);
    setHue(h);
    setSat(s);
    setLight(l);
  }, [open, color]);

  // Position before paint so it never flashes in the wrong place.
  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  // Close on outside click / Escape, and keep the portal anchored during scroll.
  useEffect(() => {
    if (!open) return;
    const handlePointerOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerOutside);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerOutside);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, reposition]);

  // Paint the hue/saturation wheel at the current lightness. Redraws only when
  // the panel opens or lightness changes — hue/saturation picks just move the
  // indicator dot, which is a separate cheap overlay.
  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const cx = WHEEL_SIZE / 2;
    const cy = WHEEL_SIZE / 2;
    const imageData = ctx.createImageData(WHEEL_SIZE, WHEEL_SIZE);
    for (let y = 0; y < WHEEL_SIZE; y++) {
      for (let x = 0; x < WHEEL_SIZE; x++) {
        const dx = x - cx;
        const dy = y - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const idx = (y * WHEEL_SIZE + x) * 4;
        if (dist > WHEEL_RADIUS) continue; // leave transparent outside the circle
        const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
        const pointHue = (angle + 360) % 360;
        const pointSat = Math.min(100, (dist / WHEEL_RADIUS) * 100);
        const hex = hsl2hex(pointHue, pointSat, light);
        imageData.data[idx] = parseInt(hex.slice(1, 3), 16);
        imageData.data[idx + 1] = parseInt(hex.slice(3, 5), 16);
        imageData.data[idx + 2] = parseInt(hex.slice(5, 7), 16);
        imageData.data[idx + 3] = 255;
      }
    }
    ctx.putImageData(imageData, 0, 0);
  }, [open, light]);

  const pickFromPointer = useCallback(
    (event: ReactPointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const cx = WHEEL_SIZE / 2;
      const cy = WHEEL_SIZE / 2;
      const x = ((event.clientX - rect.left) / rect.width) * WHEEL_SIZE;
      const y = ((event.clientY - rect.top) / rect.height) * WHEEL_SIZE;
      const dx = x - cx;
      const dy = y - cy;
      const dist = Math.min(WHEEL_RADIUS, Math.sqrt(dx * dx + dy * dy));
      const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
      const newHue = (angle + 360) % 360;
      const newSat = (dist / WHEEL_RADIUS) * 100;
      setHue(newHue);
      setSat(newSat);
      onChange(hsl2hex(newHue, newSat, light));
    },
    [light, onChange],
  );

  const handleLightChange = useCallback(
    (newLight: number) => {
      setLight(newLight);
      onChange(hsl2hex(hue, sat, newLight));
    },
    [hue, sat, onChange],
  );

  const dotAngleRad = (hue * Math.PI) / 180;
  const dotRadius = (sat / 100) * WHEEL_RADIUS;
  const dotLeft = WHEEL_SIZE / 2 + Math.cos(dotAngleRad) * dotRadius;
  const dotTop = WHEEL_SIZE / 2 + Math.sin(dotAngleRad) * dotRadius;

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        aria-controls={panelId}
        className={`h-9 w-9 shrink-0 rounded-md border p-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river focus-visible:ring-offset-1 ${
          highlighted ? "border-flima-river ring-2 ring-flima-river/50" : "border-flima-spruce/20"
        }`}
        aria-label={label}
        title={label}
      >
        <span className="block h-full w-full rounded-sm" style={{ backgroundColor: color }} aria-hidden="true" />
      </button>

      {open && coords
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              role="dialog"
              aria-label={label}
              style={{ left: coords.left, top: coords.top, width: PANEL_WIDTH, maxHeight: coords.maxHeight }}
              className="fixed z-[9999] overflow-y-auto rounded-xl border border-flima-spruce/10 bg-white p-3 shadow-panel"
            >
              <div className="relative mx-auto" style={{ width: WHEEL_SIZE, height: WHEEL_SIZE }}>
                <canvas
                  ref={canvasRef}
                  width={WHEEL_SIZE}
                  height={WHEEL_SIZE}
                  className="touch-none rounded-full [cursor:crosshair]"
                  onPointerDown={(event) => {
                    draggingRef.current = true;
                    event.currentTarget.setPointerCapture(event.pointerId);
                    pickFromPointer(event);
                  }}
                  onPointerMove={(event) => {
                    if (draggingRef.current) pickFromPointer(event);
                  }}
                  onPointerUp={(event) => {
                    draggingRef.current = false;
                    event.currentTarget.releasePointerCapture(event.pointerId);
                  }}
                />
                <span
                  className="pointer-events-none absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
                  style={{ left: dotLeft, top: dotTop, backgroundColor: color }}
                  aria-hidden="true"
                />
              </div>

              <label className="mt-3 block">
                <span className="block font-utility text-[0.6rem] uppercase tracking-wide text-flima-slate/60">
                  {light.toFixed(0)}%
                </span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={light}
                  onChange={(event) => handleLightChange(Number(event.target.value))}
                  className="mt-1 h-11 w-full cursor-pointer accent-flima-river"
                  style={{
                    background: `linear-gradient(to right, #000, ${hsl2hex(hue, sat, 50)}, #fff)`,
                  }}
                />
              </label>

              <input
                type="text"
                value={color}
                onChange={(event) => onChange(event.target.value)}
                className="mt-2 w-full min-h-9 rounded-md border border-flima-spruce/15 px-2 font-utility text-xs uppercase text-flima-slate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flima-river"
                spellCheck={false}
              />
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
